<template>
  <v-container fluid class="mt-4">
    <v-card>
      <v-card-title class="text-subtitle-1">
        PDF a Excel
        <v-spacer></v-spacer>
        <v-chip small outlined color="primary">Sin guardar en servidor</v-chip>
      </v-card-title>

      <v-card-text>
        <p class="caption grey--text mb-4">
          Sube un estado de cuenta BBVA en PDF, revisa los movimientos en la tabla y después descarga el Excel.
        </p>

        <div v-if="!file">
          <div
            :class="['dropZone', dragging ? 'dropZone-over' : '']"
            @dragenter="dragging = true"
            @dragleave="dragging = false"
            @drop.prevent="onDrop"
            @dragover.prevent
          >
            <div class="dropZone-info">
              <v-icon large color="primary">mdi-file-pdf-box</v-icon>
              <div class="dropZone-title blue--text mt-2">
                <b>Arrastra tu PDF aquí o haz clic para seleccionar</b>
              </div>
              <div class="caption mt-2">Tamaño máximo: 25 MB</div>
            </div>
            <input type="file" accept="application/pdf,.pdf" @change="onChange" />
          </div>
        </div>

        <div v-else>
          <v-alert type="info" dense text class="mb-3">
            <strong>{{ file.name }}</strong>
            ({{ formatBytes(file.size) }})
          </v-alert>

          <v-row class="mb-2">
            <v-col cols="12" sm="4">
              <v-btn
                color="primary"
                block
                :loading="cargar"
                :disabled="cargar"
                @click="procesar"
              >
                <v-icon left>mdi-table-eye</v-icon>
                Procesar y visualizar
              </v-btn>
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn
                color="success"
                block
                :loading="descargando"
                :disabled="!rows.length || cargar || descargando"
                @click="descargarExcel"
              >
                <v-icon left>mdi-microsoft-excel</v-icon>
                Descargar Excel
              </v-btn>
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn color="grey" dark block :disabled="cargar" @click="limpiarTodo">
                <v-icon left>mdi-close</v-icon>
                Quitar archivo
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-alert
          v-if="meta"
          :type="comparacion && comparacion.cuadra ? 'success' : 'info'"
          dense
          text
          class="mt-2"
        >
          {{ rows.length }} movimientos detectados
          <span v-if="meta.bbva"> (formato BBVA)</span>
          <span v-if="meta.parseo"> — parseo: {{ meta.parseo }}</span>
          <span v-if="buscar && rowsFiltradas.length !== rows.length">
            — mostrando {{ rowsFiltradas.length }} con el filtro
          </span>
        </v-alert>

        <v-card
          v-if="comparacion && comparacion.tiene_totales_pdf"
          outlined
          class="mt-4 mb-2"
        >
          <v-card-title class="text-subtitle-2 py-2">
            Total de Movimientos — Comparación
            <v-spacer></v-spacer>
            <v-chip
              small
              :color="comparacion.cuadra ? 'success' : 'error'"
              dark
            >
              {{ comparacion.cuadra ? 'Cuadra' : 'No cuadra' }}
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-3 pb-1">
            <v-row dense>
              <v-col
                v-for="(item, idx) in comparacion.items"
                :key="idx"
                cols="12"
                sm="6"
                md="3"
              >
                <v-card outlined class="pa-3" :class="item.coincide ? 'kpi-ok' : 'kpi-diff'">
                  <div class="caption grey--text text--darken-1 mb-1">{{ item.concepto }}</div>
                  <div class="body-2">
                    <span class="grey--text">PDF:</span>
                    <strong class="ml-1">{{ formatKpiValor(item.pdf, item.es_importe) }}</strong>
                  </div>
                  <div class="body-2 mt-1">
                    <span class="grey--text">Calculado:</span>
                    <strong class="ml-1">{{ formatKpiValor(item.calculado, item.es_importe) }}</strong>
                  </div>
                  <div
                    v-if="item.diferencia !== null && !item.coincide"
                    class="caption error--text mt-1"
                  >
                    Dif.: {{ formatKpiValor(item.diferencia, item.es_importe) }}
                  </div>
                  <v-icon
                    v-if="item.coincide"
                    small
                    color="success"
                    class="mt-1"
                  >mdi-check-circle</v-icon>
                  <v-icon
                    v-else-if="item.coincide === false"
                    small
                    color="error"
                    class="mt-1"
                  >mdi-alert-circle</v-icon>
                </v-card>
              </v-col>
            </v-row>

            <v-data-table
              :headers="headersComparacion"
              :items="comparacion.items"
              dense
              hide-default-footer
              class="elevation-0 mt-3 comparacion-tabla"
              :items-per-page="-1"
            >
              <template v-slot:item.pdf="{ item }">
                {{ formatKpiValor(item.pdf, item.es_importe) }}
              </template>
              <template v-slot:item.calculado="{ item }">
                {{ formatKpiValor(item.calculado, item.es_importe) }}
              </template>
              <template v-slot:item.diferencia="{ item }">
                <span :class="item.coincide ? '' : 'error--text font-weight-bold'">
                  {{ item.diferencia !== null ? formatKpiValor(item.diferencia, item.es_importe) : '—' }}
                </span>
              </template>
              <template v-slot:item.coincide="{ item }">
                <v-chip x-small :color="item.coincide ? 'success' : 'error'" dark>
                  {{ item.coincide ? 'SÍ' : 'NO' }}
                </v-chip>
              </template>
            </v-data-table>

            <v-alert
              v-if="diagnostico && diagnostico.mensajes && diagnostico.mensajes.length"
              :type="comparacion && comparacion.cuadra ? 'success' : 'warning'"
              dense
              text
              class="mt-3 mb-0"
            >
              <div v-for="(msg, midx) in diagnostico.mensajes" :key="midx" class="mb-1">
                {{ msg }}
              </div>
              <div v-if="diagnostico.revisar_manual_solo_si" class="caption mt-1">
                {{ diagnostico.revisar_manual_solo_si }}
              </div>
            </v-alert>
          </v-card-text>
        </v-card>

        <v-card
          v-if="anomalias.length"
          outlined
          class="mt-4 mb-2"
        >
          <v-card-title class="text-subtitle-2 py-2">
            Revisión dirigida ({{ anomalias.length }} filas)
            <v-spacer></v-spacer>
            <v-chip small outlined color="warning">No revisar las {{ rows.length }} filas</v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-2 pb-1">
            <p class="caption grey--text mb-2">
              Movimientos donde la descripción no coincide con la columna (cargo/abono) o posibles duplicados.
            </p>
            <v-data-table
              :headers="headersAnomalias"
              :items="anomalias"
              dense
              class="elevation-0"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            >
              <template v-slot:item.CARGOS="{ item }">
                <span v-if="item.CARGOS">{{ formatMonto(item.CARGOS) }}</span>
              </template>
              <template v-slot:item.ABONOS="{ item }">
                <span v-if="item.ABONOS">{{ formatMonto(item.ABONOS) }}</span>
              </template>
              <template v-slot:item.motivos="{ item }">
                <span class="caption">{{ item.motivos.join(' · ') }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-row v-if="rows.length" class="mt-2">
          <v-col cols="12" md="6" lg="4">
            <v-text-field
              v-model="buscar"
              label="Buscar en movimientos"
              placeholder="Fecha, código, descripción, referencia, monto..."
              prepend-inner-icon="mdi-magnify"
              filled
              dense
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" lg="8" class="d-flex align-center">
            <span class="caption grey--text" v-if="buscar">
              {{ rowsFiltradas.length }} de {{ rows.length }} registros coinciden
            </span>
          </v-col>
        </v-row>

        <v-data-table
          v-if="rows.length"
          :headers="headers"
          :items="rowsFiltradas"
          dense
          class="elevation-1 mt-2"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          fixed-header
          height="480"
        >
          <template v-slot:item.CARGOS="{ item }">
            <span v-if="item.CARGOS">{{ formatMonto(item.CARGOS) }}</span>
          </template>
          <template v-slot:item.ABONOS="{ item }">
            <span v-if="item.ABONOS">{{ formatMonto(item.ABONOS) }}</span>
          </template>
          <template v-slot:item.DESCRIPCION="{ item }">
            <span class="text-truncate d-inline-block" style="max-width: 200px" :title="item.DESCRIPCION">
              {{ item.DESCRIPCION }}
            </span>
          </template>
          <template v-slot:item.REFERENCIA="{ item }">
            <span class="text-truncate d-inline-block" style="max-width: 280px" :title="item.REFERENCIA">
              {{ item.REFERENCIA }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <carga v-if="cargar || descargando" />
  </v-container>
</template>

<script>
import carga from '@/components/alertas/carga.vue';
import validarErrores from '@/mixins/validarErrores';

export default {
  name: 'PDFTOEXCEL',

  components: { carga },

  mixins: [validarErrores],

  data: function () {
    return {
      file: null,
      dragging: false,
      cargar: false,
      descargando: false,
      rows: [],
      meta: null,
      excelFilename: 'pdf-convertido.xlsx',
      buscar: '',
      comparacion: null,
      diagnostico: null,
      anomalias: [],
      headersAnomalias: [
        { text: '#', value: 'indice', width: '50px' },
        { text: 'OPER', value: 'OPER', width: '80px' },
        { text: 'COD', value: 'COD', width: '60px' },
        { text: 'DESCRIPCION', value: 'DESCRIPCION' },
        { text: 'CARGOS', value: 'CARGOS', align: 'end', width: '90px' },
        { text: 'ABONOS', value: 'ABONOS', align: 'end', width: '90px' },
        { text: 'Motivo', value: 'motivos', sortable: false },
      ],
      headersComparacion: [
        { text: 'Concepto', value: 'concepto' },
        { text: 'PDF (estado de cuenta)', value: 'pdf', align: 'end' },
        { text: 'Calculado', value: 'calculado', align: 'end' },
        { text: 'Diferencia', value: 'diferencia', align: 'end' },
        { text: '¿Cuadra?', value: 'coincide', align: 'center', sortable: false },
      ],
      headers: [
        { text: 'OPER', value: 'OPER', width: '90px' },
        { text: 'LIQUID', value: 'LIQUID', width: '90px' },
        { text: 'COD', value: 'COD', width: '70px' },
        { text: 'DESCRIPCION', value: 'DESCRIPCION' },
        { text: 'REFERENCIA', value: 'REFERENCIA' },
        { text: 'CARGOS', value: 'CARGOS', align: 'end', width: '100px' },
        { text: 'ABONOS', value: 'ABONOS', align: 'end', width: '100px' },
        { text: 'OPERACIÓN', value: 'OPERACIÓN', width: '100px' },
        { text: 'LIQUIDACION', value: 'LIQUIDACION', width: '110px' },
      ],
    };
  },

  computed: {
    rowsFiltradas: function () {
      var self = this;
      if (!this.buscar || !String(this.buscar).trim()) {
        return this.rows;
      }
      var q = String(this.buscar).trim().toLowerCase();
      return this.rows.filter(function (row) {
        return self.headers.some(function (col) {
          var val = row[col.value];
          if (val === null || val === undefined || val === '') {
            return false;
          }
          return String(val).toLowerCase().indexOf(q) !== -1;
        });
      });
    },
  },

  methods: {
    onChange: function (e) {
      var target = e.target;
      var selected = target && target.files && target.files[0];
      if (selected) this.setFile(selected);
      if (target) target.value = '';
    },

    onDrop: function (e) {
      this.dragging = false;
      var dt = e.dataTransfer;
      var selected = dt && dt.files && dt.files[0];
      if (selected) this.setFile(selected);
    },

    setFile: function (selected) {
      var esPdf =
        selected.type === 'application/pdf' ||
        selected.name.toLowerCase().endsWith('.pdf');
      if (!esPdf) {
        return this.validarErrorDirecto('Solo se permiten archivos PDF');
      }
      if (selected.size > 25 * 1024 * 1024) {
        return this.validarErrorDirecto('El PDF no debe superar 25 MB');
      }
      this.file = selected;
      this.rows = [];
      this.meta = null;
      this.buscar = '';
      this.comparacion = null;
      this.diagnostico = null;
      this.anomalias = [];
    },

    limpiarTodo: function () {
      this.file = null;
      this.rows = [];
      this.meta = null;
      this.excelFilename = 'pdf-convertido.xlsx';
      this.buscar = '';
      this.comparacion = null;
      this.diagnostico = null;
      this.anomalias = [];
    },

    formatBytes: function (bytes) {
      if (!bytes) return '0 B';
      var k = 1024;
      var sizes = ['B', 'KB', 'MB'];
      var i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    formatMonto: function (val) {
      if (val === '' || val === null || val === undefined) return '';
      return this.formatKpiValor(val, true);
    },

    formatKpiValor: function (val, esImporte) {
      if (val === '' || val === null || val === undefined) return '—';
      var n = Number(val);
      if (isNaN(n)) return String(val);
      if (esImporte) {
        return n.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
          minimumFractionDigits: 2,
        });
      }
      return n.toLocaleString('es-MX');
    },

    parseFilename: function (contentDisposition) {
      if (!contentDisposition) return this.excelFilename;
      var utf = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
      if (utf) return decodeURIComponent(utf[1]);
      var plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
      return plain ? plain[1] : this.excelFilename;
    },

    procesar: function () {
      var self = this;
      if (!this.file) {
        return this.validarErrorDirecto('Selecciona un PDF primero');
      }

      var formData = new FormData();
      formData.append('file', this.file);
      this.cargar = true;
      this.rows = [];
      this.meta = null;
      this.buscar = '';
      this.comparacion = null;
      this.diagnostico = null;
      this.anomalias = [];

      this.$http
        .post('pdf.to.excel.preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(function (response) {
          self.rows = response.data.rows || [];
          self.meta = response.data.meta || null;
          self.comparacion = response.data.comparacion || null;
          self.diagnostico = response.data.diagnostico || null;
          self.anomalias = response.data.anomalias || [];
          self.excelFilename = response.data.filename || 'pdf-convertido.xlsx';
          if (!self.rows.length) {
            self.validarErrorDirecto('No se encontraron movimientos en el PDF');
          } else {
            var msg = 'Se cargaron ' + self.rows.length + ' movimientos.';
            if (self.comparacion && self.comparacion.tiene_totales_pdf) {
              if (self.comparacion.cuadra) {
                msg += ' Los totales cuadran con el PDF.';
              } else if (self.anomalias.length) {
                msg +=
                  ' Los totales no cuadran; revisa solo las ' +
                  self.anomalias.length +
                  ' filas marcadas en anomalías.';
              } else {
                msg += ' Revisa la comparación de totales.';
              }
            }
            self.validarSuccess(msg);
          }
        })
        .catch(function (error) {
          self.validarError(error);
        })
        .finally(function () {
          self.cargar = false;
        });
    },

    descargarExcel: function () {
      var self = this;
      if (!this.rows.length) {
        return this.validarErrorDirecto('Primero procesa el PDF para ver los movimientos');
      }

      this.descargando = true;

      this.$http
        .post(
          'pdf.to.excel.download',
          {
            rows: this.rows,
            filename: this.file ? this.file.name : this.excelFilename,
            comparacion: this.comparacion,
          },
          { responseType: 'blob' }
        )
        .then(function (response) {
          var blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          var url = window.URL.createObjectURL(blob);
          var link = document.createElement('a');
          link.href = url;
          link.download = self.parseFilename(response.headers['content-disposition']);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          self.validarSuccess('Excel descargado correctamente');
        })
        .catch(function (error) {
          var errData = error.response && error.response.data;
          if (errData instanceof Blob) {
            errData.text().then(function (text) {
              try {
                var json = JSON.parse(text);
                self.validarErrorDirecto(json.message || 'Error al descargar el Excel');
              } catch (e) {
                self.validarError(error);
              }
            });
            return;
          }
          self.validarError(error);
        })
        .finally(function () {
          self.descargando = false;
        });
    },
  },
};
</script>

<style scoped>
.dropZone {
  position: relative;
  border: 2px dashed #1976d2;
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  background: #f5f9ff;
  transition: background 0.2s;
}
.dropZone-over {
  background: #e3f2fd;
}
.dropZone input[type='file'] {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.dropZone-info {
  pointer-events: none;
}
.kpi-ok {
  border-left: 4px solid #4caf50 !important;
}
.kpi-diff {
  border-left: 4px solid #f44336 !important;
}
.comparacion-tabla {
  border-top: 1px solid #e0e0e0;
}
</style>
